import { toast } from "sonner";
import type { AccountDeletionResult } from "@/api/compliance";

/**
 * FP-126 (BEN-141): after a delete, the server's two sentences were on screen for
 * under 600 ms. Settings toasted them and then signed out, and Clerk's sign-out
 * reloads the page, which unmounts the toaster. Lost with them: the `retained`
 * disclosure (what de-identified records we keep, shown "at the moment it becomes
 * true", stored-data-inventory §8), and on the incomplete path the 12-second
 * "Some cleanup with an outside provider did not complete" message.
 *
 * Settings stashes them here before signing out. Whichever runs first after that
 * takes them exactly once: the app's mount after a reload, or Settings itself if
 * there was no reload.
 */
const KEY = "fomo-fi:account-deleted-notice";

type Notice = Pick<AccountDeletionResult, "detail" | "retained" | "complete">;
type Store = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export function stashDeletedNotice(store: Store, notice: Notice): void {
  try {
    store.setItem(KEY, JSON.stringify(notice));
  } catch {
    // Storage blocked: the toast still shows if there's no reload.
  }
}

export function takeDeletedNotice(store: Store): Notice | null {
  try {
    const raw = store.getItem(KEY);
    if (raw === null) return null;
    store.removeItem(KEY);
    const n = JSON.parse(raw) as Partial<Notice>;
    if (typeof n.detail !== "string" || !n.detail) return null;
    return {
      detail: n.detail,
      retained: typeof n.retained === "string" ? n.retained : "",
      complete: n.complete === true,
    };
  } catch {
    return null;
  }
}

/** Shows a stashed notice, if one is waiting. Safe to call more than once. */
export function showPendingDeletedNotice(store: Store | undefined): void {
  if (!store) return;
  const n = takeDeletedNotice(store);
  if (!n) return;
  // Paul (step E, 2026-10-07): the confirmation and the retention line stay until the
  // user closes them. The retention line is a stored-data-inventory item shown at the
  // moment it becomes true; a timed toast let it go before anyone could read it (WCAG
  // 2.2.1; the reasoning of mobile's FP-115 note).
  toast.success(n.detail, {
    description: n.retained || undefined,
    duration: Infinity,
    closeButton: true,
  });
}
